import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks/useAuth';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Logo } from '@/components/Logo';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Badge } from '@/components/ui/badge';
import { 
  User, 
  LogOut, 
  LayoutDashboard, 
  Menu, 
  ChevronDown,
  Book,
  Target,
  Building2,
  Handshake,
  Mail,
  Database,
  Wrench,
  FileText,
  PenTool,
  Lightbulb,
} from 'lucide-react';
import { useState } from 'react';
import { StandardsSearch } from '@/components/data-standards/StandardsSearch';

export function Navbar() {
  const { user, isAdmin, profile, signOut } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [standardsOpen, setStandardsOpen] = useState(true);
  const [resourcesOpen, setResourcesOpen] = useState(true);

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  const getInitials = () => {
    if (profile?.full_name) {
      return profile.full_name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    }
    if (user?.email) {
      return user.email.charAt(0).toUpperCase();
    }
    return 'U';
  };

  const displayName = profile?.full_name || user?.email || 'User';

  return (
    <header className="sticky top-0 z-50 w-full border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="container flex h-14 items-center justify-between">
        <div className="flex items-center gap-8">
          <Link to="/" className="flex items-center gap-2" aria-label="AutomotiveAPI - Go to homepage">
            <Logo size="md" />
            <span className="font-sans font-semibold text-lg hidden sm:block">AutomotiveAPI</span>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            {/* Standards Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="gap-1">
                  Standards
                  <ChevronDown className="h-3 w-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-64">
                <DropdownMenuItem asChild>
                  <Link to="/api-standards" className="flex items-center gap-3">
                    <Database className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <p className="font-medium">API Standards</p>
                      <p className="text-xs text-muted-foreground">Browse all data standards</p>
                    </div>
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/api-standards/builder" className="flex items-center gap-3">
                    <Wrench className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <p className="font-medium">API Schema Builder</p>
                      <p className="text-xs text-muted-foreground">Create custom schemas</p>
                    </div>
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/adf-standard" className="flex items-center gap-3">
                    <FileText className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <p className="font-medium">ADF Standard</p>
                      <p className="text-xs text-muted-foreground">Auto-lead Data Format</p>
                    </div>
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/adf-standard/builder" className="flex items-center gap-3">
                    <PenTool className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <p className="font-medium">ADF Builder</p>
                      <p className="text-xs text-muted-foreground">Build ADF templates</p>
                    </div>
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/proposals" className="flex items-center gap-3">
                    <Lightbulb className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <p className="font-medium">Community Proposals</p>
                      <p className="text-xs text-muted-foreground">Vote on new standards</p>
                    </div>
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Resources Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="gap-1">
                  Resources
                  <ChevronDown className="h-3 w-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-56">
                <DropdownMenuItem asChild>
                  <Link to="/docs" className="flex items-center gap-3">
                    <Book className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <p className="font-medium">Documentation</p>
                      <p className="text-xs text-muted-foreground">Guides and references</p>
                    </div>
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/use-cases" className="flex items-center gap-3">
                    <Target className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <p className="font-medium">Use Cases</p>
                      <p className="text-xs text-muted-foreground">Real-world applications</p>
                    </div>
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/about" className="flex items-center gap-3">
                    <Building2 className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <p className="font-medium">About</p>
                      <p className="text-xs text-muted-foreground">Our story and mission</p>
                    </div>
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/partners" className="flex items-center gap-3">
                    <Handshake className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <p className="font-medium">Partners</p>
                      <p className="text-xs text-muted-foreground">The builders</p>
                    </div>
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/contact" className="flex items-center gap-3">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <p className="font-medium">Contact</p>
                      <p className="text-xs text-muted-foreground">Get in touch</p>
                    </div>
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </nav>
        </div>

        <div className="flex items-center gap-3">
          {/* Search - Desktop */}
          <div className="hidden md:block">
            <StandardsSearch />
          </div>
          
          {/* Search - Mobile */}
          <StandardsSearch variant="compact" className="md:hidden" />
          
          {user && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="relative h-8 w-8 rounded-full">
                  <Avatar className="h-8 w-8">
                    {profile?.avatar_url ? (
                      <AvatarImage src={profile.avatar_url} alt={displayName} />
                    ) : (
                      <img 
                        src="https://api.dicebear.com/7.x/avataaars/svg?seed=default&backgroundColor=b6e3f4" 
                        alt={displayName}
                        className="h-full w-full object-cover"
                      />
                    )}
                    <AvatarFallback className="bg-secondary text-foreground text-sm">
                      {getInitials()}
                    </AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="flex items-center justify-start gap-2 p-2">
                  <div className="flex flex-col space-y-1 leading-none">
                    <p className="text-sm font-medium">{displayName}</p>
                    {profile?.full_name && user?.email && (
                      <p className="text-xs text-muted-foreground">{user.email}</p>
                    )}
                    {isAdmin && (
                      <p className="text-xs text-muted-foreground">Administrator</p>
                    )}
                  </div>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/dashboard" className="flex items-center">
                    <LayoutDashboard className="mr-2 h-4 w-4" />
                    Dashboard
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/dashboard/account" className="flex items-center">
                    <User className="mr-2 h-4 w-4" />
                    Account Settings
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleSignOut} className="text-destructive">
                  <LogOut className="mr-2 h-4 w-4" />
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          {/* Mobile menu */}
          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <SheetTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="md:hidden"
                aria-label="Open menu"
              >
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[calc(100vw-48px)] max-w-sm pt-12 px-4">
              <nav className="flex flex-col gap-1">
                {/* Search in mobile menu - full width */}
                <div className="py-3">
                  <StandardsSearch className="w-full" />
                </div>
                
                {/* Full-width separator */}
                <div className="-mx-4 my-2 border-t border-border" />
                
                {/* Standards Collapsible Section */}
                <Collapsible open={standardsOpen} onOpenChange={setStandardsOpen}>
                  <CollapsibleTrigger className="flex items-center justify-between w-full px-1 py-2.5 text-sm font-medium text-foreground hover:bg-secondary rounded-md transition-colors">
                    <span>Standards</span>
                    <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform duration-200 ${standardsOpen ? 'rotate-180' : ''}`} />
                  </CollapsibleTrigger>
                  <CollapsibleContent className="pl-2">
                    <Link
                      to="/api-standards"
                      className="flex items-center gap-2 px-2 py-2.5 text-sm text-muted-foreground hover:text-foreground hover:bg-secondary rounded-md transition-colors"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <Database className="h-4 w-4" />
                      API Standards
                    </Link>
                    <Link
                      to="/api-standards/builder"
                      className="flex items-center gap-2 px-2 py-2.5 text-sm text-muted-foreground hover:text-foreground hover:bg-secondary rounded-md transition-colors"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <Wrench className="h-4 w-4" />
                      API Schema Builder
                    </Link>
                    <Link
                      to="/adf-standard"
                      className="flex items-center gap-2 px-2 py-2.5 text-sm text-muted-foreground hover:text-foreground hover:bg-secondary rounded-md transition-colors"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <FileText className="h-4 w-4" />
                      ADF Standard
                    </Link>
                    <Link
                      to="/adf-standard/builder"
                      className="flex items-center gap-2 px-2 py-2.5 text-sm text-muted-foreground hover:text-foreground hover:bg-secondary rounded-md transition-colors"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <PenTool className="h-4 w-4" />
                      ADF Builder
                    </Link>
                    <Link
                      to="/proposals"
                      className="flex items-center gap-2 px-2 py-2.5 text-sm text-muted-foreground hover:text-foreground hover:bg-secondary rounded-md transition-colors"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <Lightbulb className="h-4 w-4" />
                      Community Proposals
                    </Link>
                  </CollapsibleContent>
                </Collapsible>
                
                {/* Resources Collapsible Section */}
                <Collapsible open={resourcesOpen} onOpenChange={setResourcesOpen}>
                  <CollapsibleTrigger className="flex items-center justify-between w-full px-1 py-2.5 text-sm font-medium text-foreground hover:bg-secondary rounded-md transition-colors">
                    <span>Resources</span>
                    <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform duration-200 ${resourcesOpen ? 'rotate-180' : ''}`} />
                  </CollapsibleTrigger>
                  <CollapsibleContent className="pl-2">
                    <Link
                      to="/docs"
                      className="flex items-center gap-2 px-2 py-2.5 text-sm text-muted-foreground hover:text-foreground hover:bg-secondary rounded-md transition-colors"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <Book className="h-4 w-4" />
                      Documentation
                    </Link>
                    <Link
                      to="/use-cases"
                      className="flex items-center gap-2 px-2 py-2.5 text-sm text-muted-foreground hover:text-foreground hover:bg-secondary rounded-md transition-colors"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <Target className="h-4 w-4" />
                      Use Cases
                    </Link>
                    <Link
                      to="/about"
                      className="flex items-center gap-2 px-2 py-2.5 text-sm text-muted-foreground hover:text-foreground hover:bg-secondary rounded-md transition-colors"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <Building2 className="h-4 w-4" />
                      About
                    </Link>
                    <Link
                      to="/partners"
                      className="flex items-center gap-2 px-2 py-2.5 text-sm text-muted-foreground hover:text-foreground hover:bg-secondary rounded-md transition-colors"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <Handshake className="h-4 w-4" />
                      Partners
                    </Link>
                    <Link
                      to="/contact"
                      className="flex items-center gap-2 px-2 py-2.5 text-sm text-muted-foreground hover:text-foreground hover:bg-secondary rounded-md transition-colors"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <Mail className="h-4 w-4" />
                      Contact
                    </Link>
                  </CollapsibleContent>
                </Collapsible>
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
